import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useWorkspace } from '../../contexts/WorkspaceContext';

/*
 * OrganizationWelcomePage — landing page after a successful checkout (or a
 * trial-only org creation if Stripe billing isn't configured yet). We pick
 * the org we landed on from either ?org_id=... or by simply fetching the
 * user's orgs and showing the most recent one.
 *
 * Phase 2: this is intentionally a celebratory placeholder. Phase 3 wires
 * the invite flow + member management directly under /organization/members.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrganizationWelcomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshWorkspaces, switchWorkspace } = useWorkspace();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams(location.search);
        const orgId = params.get('org_id');
        const res = await axios.get(`${API}/organizations/mine`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = res.data || [];
        const picked = orgId ? list.find((o) => o.id === orgId) : list[list.length - 1];
        setOrg(picked || null);
        // Refresh the WorkspaceContext's cached org list so the switcher
        // picks up the new workspace, then auto-switch into it so the user
        // lands in the correct context when they tap "Go to Tasks".
        if (picked) {
          try { await refreshWorkspaces(); } catch { /* noop */ }
          try { switchWorkspace(picked.id); } catch { /* noop */ }
        }
      } catch (e) {
        console.error('Failed to load org:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [location.search, refreshWorkspaces, switchWorkspace]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50">
        <p className="text-gray-600">Setting up your workspace…</p>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full text-center">
          <p className="text-gray-700 mb-4">We couldn't find your new workspace.</p>
          <button
            onClick={() => navigate('/settings/subscription')}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium"
          >
            Back to Subscription
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full" data-testid="org-welcome">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center text-3xl mb-4">
          🎉
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">{org.name} is ready</h1>
        <p className="text-gray-600 mb-5">
          Your team workspace is up. Invite your teammates to start sharing
          tasks, contacts, and categories.
        </p>
        <div className="bg-gray-50 rounded-lg p-3 mb-5 text-sm">
          <div className="flex justify-between mb-1">
            <span className="text-gray-600">Plan</span>
            <span className="font-medium text-gray-900 capitalize">{org.subscription_status}</span>
          </div>
          <div className="flex justify-between mb-1">
            <span className="text-gray-600">Seats</span>
            <span className="font-medium text-gray-900">{org.seat_count}</span>
          </div>
          {org.trial_ends_at && (
            <div className="flex justify-between">
              <span className="text-gray-600">Trial ends</span>
              <span className="font-medium text-gray-900">
                {new Date(org.trial_ends_at).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <button
            onClick={() => navigate(`/organization/members?org_id=${org.id}`)}
            data-testid="org-welcome-invite-cta"
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-2.5 rounded-xl font-medium hover:opacity-95 transition-opacity"
          >
            Invite your team
          </button>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-2.5 rounded-xl font-medium transition-colors"
          >
            Go to Tasks
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrganizationWelcomePage;
